import { Dialog } from "@headlessui/react";
import { ModalFoterBox, ModalHeaderBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { forwardRef, useState, useEffect, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import TableList, { GridRow } from "../../components/table/TableList";
import pointDataQuery from "../../firestore/PointData.query";
import { auth } from "../../firebase";
import { PointTypeId } from "../../config/const";

export type MessagePointSettingModalProp = {
  point?: PointForm;
  onSubmit: (data: PointForm) => void;
  onSelectCancel?: () => void;
}

export interface PointForm {
  id: string;
  categoryId: number;
  categoryName: string;
  typeId: string;
  typeName: string;
  pointName: string;
  point: number;
}

const MessagePointSettingModal = forwardRef(({
  open,
  toggle,
  closeOnClickOutside,
  point,
  onSubmit,
}: ModalProp & MessagePointSettingModalProp, ref) => {
  const pointTypes = [
    { id: "全て", name: "全て" },
    { id: PointTypeId.VOLUNTEER_COURSE, name: "ボランティア講座参加" },
    { id: PointTypeId.EVENT_PARTICIPATION, name: "イベント参加" }
  ];

  const col = [
    { field: "radio", headerName: '', style: "w-max-12", },
    { field: "pointName", headerName: "ポイント名", style: "w-1/3", },
    { field: "typeName", headerName: "ポイントタイプ", style: "w-1/3", },
    { field: "point", headerName: "ポイント数", style: "w-1/6 text-right", },
  ];

  const { handleSubmit, reset } = useForm<PointForm>({
    defaultValues: {
      ...point,
    },
  });

  const [selectedToken, setSelectedToken] = useState<string | null>(null);
  const [selectedPointType, setSelectedPointType] = useState<string>("全て");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredData, setFilteredData] = useState<PointForm[]>([]);
  const [initialSelectedToken, setInitialSelectedToken] = useState<string | null>(null);
  const [originalData, setOriginalData] = useState<PointForm[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const currentUser = auth.currentUser;
      const data = await pointDataQuery.getPtAwardedSettingsByTypeId(
        currentUser?.tenantId!,
        PointTypeId.VOLUNTEER_COURSE,
        PointTypeId.EVENT_PARTICIPATION
      );
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const transformedData = data
        .filter(item => {
          const from = item.validPeriodDateFrom ? new Date(item.validPeriodDateFrom) : null;
          const to = item.validPeriodDateTo ? new Date(item.validPeriodDateTo) : null;
  
          if (from) {
            const fromDate = new Date(from.getFullYear(), from.getMonth(), from.getDate());
            if (today < fromDate) {
              return false;
            }
          }
          if (to) {
            const toDate = new Date(to.getFullYear(), to.getMonth(), to.getDate());
            if (today > toDate) {
              return false;
            }
          }
          return true;
        })
        .map(item => ({
          id: item.id,
          categoryId: item.categoryId,
          categoryName: item.categoryName,
          typeId: item.typeId,
          typeName: item.typeName,
          pointName: item.name,
          point: item.basePoint!,
        }))
        .sort((a, b) => a.pointName.localeCompare(b.pointName)); // nameの昇順
  
      setOriginalData(transformedData);
      setFilteredData(transformedData);
    };
  
    fetchData();
  }, []);
  
  useEffect(() => {
    const filtered = originalData.filter(item => 
      (selectedPointType === "全て" || item.typeId === selectedPointType) && 
      (searchTerm.length < 2 || item.pointName?.includes(searchTerm))
    );
    setFilteredData(filtered);
  }, [selectedPointType, searchTerm, originalData]);

  useEffect(() => {
    if (point) {
      setSelectedToken(point.id);
    }
    setInitialSelectedToken(point ? point.id : null);
  }, [open, point]);

  const row: GridRow[] = filteredData.map((item) => ({
    rowData: {
      radio: (
        <input
          id={`${item.id}`}
          name="pointSelection"
          type="radio"
          onChange={() => handleSelectToken(item.id)}
          checked={selectedToken === item.id}
        />
      ),
      typeName: item.typeName,
      pointName: item.pointName,
      point: (
        <div className="text-right">
          {item.point}
        </div>
      ),
    }
  }));

  const handleCancel = () => {
    setSelectedToken(initialSelectedToken);
    toggle();
  };

  const handleSelectToken = (id: string) => {
    setSelectedToken(id);
  };

  useImperativeHandle(ref, () => ({
    resetForm: () => {
      setSelectedToken(null);
      reset({
        id: "",
        categoryId: 0,
        categoryName: "",
        typeId: "",
        typeName: "",
        pointName: "",
        point: 0
      });
    }
  }));

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handlePointTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPointType(e.target.value);
  };

  const handlePointSettingSubmit = () => {
    const selectedData = filteredData.find(item => selectedToken === item.id);
    if (selectedData) {
      onSubmit(selectedData);
    }
    toggle();
  };

  return (
    <Modal
      open={open}
      toggle={toggle}
      closeOnClickOutside={closeOnClickOutside}
    >
      <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:w-full max-w-screen-md">
        <div className="px-2 pb-4 sm:p-4">
          <div className="text-center mt-3 mb-5 px-1">
            <ModalHeaderBox title={"ポイント設定選択"} />
          </div>
          <div className="mb-1 px-1">
            ポイントタイプ
          </div>
          <div className="px-1">
            <select
              value={selectedPointType}
              onChange={handlePointTypeChange}
              className="w-64 rounded-md border px-3 py-2 border-gray-300 shadow-sm sm:text-sm"
            >
              {pointTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          <div className="my-4 flex space-x-2 items-center px-1">
            <div className="relative flex focus-within:z-10">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
              <input
                onChange={handleSearch}
                className="w-64 rounded-md pl-10 focus:border-indigo-500 focus:ring-indigo-500 border px-3 py-2 border-gray-300 shadow-sm sm:text-sm"
                placeholder="ポイント名で検索"
              />
            </div>
            <div>
              ※2文字以上の入力で検索できます。
            </div>
          </div>
          <div className={"max-h-[calc(100vh_-_20rem)] overflow-auto pb-4 pt-1"}>
            <TableList
              gridCol={col}
              gridRow={row}
              sort={false}
              footer={false}
            />
          </div>
        </div>
        <ModalFoterBox>
          <Button
            className="mx-2 mt-1 w-[132px]"
            outline
            onClick={handleCancel}
            children={"キャンセル"}
          />
          <Button
            className="mt-1 mr-2 w-[132px]"
            color="indigo"
            onClick={handleSubmit(handlePointSettingSubmit)}
            children={"選択"}
          />
        </ModalFoterBox>
      </Dialog.Panel>
    </Modal>
  );
});

MessagePointSettingModal.displayName = "MessagePointSettingModal";
export default MessagePointSettingModal;